import {
  AggregationModelEnum,
  IPriceResSchema,
  IPricesAndOptionsReqSchema,
  IProduct,
  IRateReqSchemaUI,
  IRateResSchema,
  OfferingTypesEnum,
  PercentOfTotalConfigTypeEnum,
  ProductTypeEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { transformValueToMinimumTwoDecimalPlaces } from '.';
import { RATE_BILLING_FREQUENCY_MAP } from '../constants/offerings';

export const convertRateResToReq = (
  rate: IRateResSchema,
  products: IProduct[],
  defaultCurrency: string,
  type?: OfferingTypesEnum,
): IRateReqSchemaUI => {
  const rateProducts: Record<string, IPricesAndOptionsReqSchema> =
    products.reduce(
      (acc: Record<string, IPricesAndOptionsReqSchema>, curr: IProduct) => {
        const prices = rate.prices || [];
        const currentOption = rate.options?.productOptions?.find(
          ({ productId }) => productId === curr.id,
        );

        const temporaryPayload = {
          ...acc,
          [curr.id!]: {
            productType: curr.productType,
            prices: prices
              .filter(
                (price: IPriceResSchema) => price?.product?.id === curr.id,
              )
              .map(
                (price: IPriceResSchema) =>
                  ({
                    id: price.id,
                    from: price.from,
                    to: price.to,
                    amount: transformValueToMinimumTwoDecimalPlaces(
                      price.amount,
                    ),
                    description: `${price.description || ''}`,
                    productId: curr.id,
                    priceModel: price.priceModel,
                    blockSize: price.blockSize || null,
                  }) as any as IPriceResSchema,
              ),
            aggregationModel:
              curr.productType === ProductTypeEnum.USAGE
                ? currentOption?.aggregationModel || AggregationModelEnum.SUM
                : undefined,
          },
        };

        return temporaryPayload;
      },
      {},
    );

  const defaultPercentOfTotalConfig = {
    type: PercentOfTotalConfigTypeEnum.ALL_ELIGIBLE,
    minimumTotal: null,
    maximumTotal: null,
  };

  let billingFrequencyInMonths: Maybe<number> = null;
  if (rate.billingFrequencyInMonths) {
    billingFrequencyInMonths = rate.billingFrequencyInMonths;
  } else {
    billingFrequencyInMonths = rate.billingFrequency
      ? RATE_BILLING_FREQUENCY_MAP[rate.billingFrequency]().month
      : null;
  }
  let percentOfTotalConfig: Maybe<IRateResSchema['percentOfTotalConfig']> =
    null;
  if (type === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL) {
    percentOfTotalConfig = rate.percentOfTotalConfig
      ? rate.percentOfTotalConfig
      : defaultPercentOfTotalConfig;
  }

  return {
    id: rate.id,
    customId: rate.customId,
    name: rate.name || '',
    description: rate.description || '',
    status: rate.status || '',
    currency: rate.currency || defaultCurrency,
    billingFrequency: rate.billingFrequency || null,
    billingFrequencyInMonths,
    usageBillingFrequency: rate.usageBillingFrequency || null,
    startDate: rate.startDate,
    endDate: rate.endDate,
    rateType: rate.rateType,
    prices:
      rate.prices?.map((price) => ({
        ...price,
        productId: price.product?.id,
      })) || [],
    products: rateProducts,
    quotable: rate.quotable,
    subscriptionTiming: rate.subscriptionTiming || null,
    // Since internalDescription field can be null/undefined setting it as empty string
    // to make it easier to handle dirty state. Eventually it will be null if empty when we make api calls
    internalDescription: rate.internalDescription || '',
    minCommitConfig:
      type === OfferingTypesEnum.MIN_COMMIT ? rate.minCommitConfig : null,
    percentOfTotalConfig,
    options: {
      priceDisplay: rate.options?.priceDisplay,
      productOptions: rate.options?.productOptions || [],
      tokenAllocationFrequencyInMonth:
        rate.options?.tokenAllocationFrequencyInMonth ?? null,
    },
    revenueChannels: rate.revenueChannels || ['SALES'],
    customFields: rate.customFields || {},
  };
};

export const checkIfProductsHaveBuckets = (products: IProduct[]) =>
  products.some((product) => product.bucketId !== null);
